import React, { useState, useEffect, useRef, useCallback } from "react";
import StudioHero from "../../components/StudioHero";
import StudioGenerateButton from "../../components/StudioGenerateButton";
import {
  runStudioWorkflow,
  getStudioWorkflowJob,
  getStudioWorkflowJobDetail,
} from "../../../../../actions/studios";
import { getUroSocket } from "../../../../../services/URO";
import { JobDetailModal } from "../../../../../components/JobDetailView";
import { uploadImage } from "../../../../../actions/imageUpload";
import secureAxios from "../../../../../actions/axios/secureAxios";
import {
  ENVIRONMENTS, RESOLUTIONS, ASPECT_RATIOS, POSES, ACCESSORIES, CHARACTER_CREATOR_WORKFLOW_ID,
} from "./options";
import "./ClothingTryOnStudio.css";

const WORKFLOW_ID = "86588165-6e53-4bca-9e5c-24d546b414ed";

// ── Fetch completed Character Creator generations for this client ──

function useCharacterModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await secureAxios.get(
          `/workflows/workflow-jobs?workflow_definition_id=${CHARACTER_CREATOR_WORKFLOW_ID}&status=completed&limit=50`
        );
        const jobs = data?.data?.items || data?.data || [];
        const items = jobs
          .map((j) => {
            let raw = j.results;
            if (typeof raw === "string") { try { raw = JSON.parse(raw); } catch (_) { return null; } }
            const results = raw?.results || (Array.isArray(raw) ? raw : null);
            if (!Array.isArray(results) || results.length === 0) return null;
            const img = results[0];
            return {
              id: j.workflow_job_id,
              imageUrl: img.content || img.external_url,
              metadata: img.additional_params || {},
            };
          })
          .filter(Boolean);
        setModels(items);
      } catch (err) {
        console.error("[ClothingTryOn] Failed to fetch character models:", err?.response?.status, err?.response?.data || err.message);
        setModels([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { models, loading };
}

// ── Chip / option selectors ──────────────────────────────────────

function ChipSelector({ label, options, value, onChange, disabled }) {
  return (
    <div className="cto-field">
      <span className="cto-field-label">{label}</span>
      <div className="cto-chips">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`cto-chip ${value === opt.value ? "cto-chip-active" : ""}`}
            onClick={() => onChange(opt.value)}
            type="button"
            disabled={disabled}
            title={opt.description || opt.title}
          >
            {opt.title}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Visual pose grid ─────────────────────────────────────────────

function PoseGrid({ options, value, onChange }) {
  return (
    <div className="cto-field">
      <span className="cto-field-label">Pose / Action</span>
      <div className="cto-pose-grid">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`cto-pose-card ${value === opt.value ? "cto-pose-card-active" : ""}`}
            onClick={() => onChange(opt.value)}
            type="button"
            title={opt.description || opt.title}
          >
            <img src={opt.image_url} alt={opt.title} loading="lazy" />
            <span className="cto-pose-card-label">{opt.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Model selector with pagination ───────────────────────────────

const MODELS_PER_PAGE = 5;

function ModelSelector({ models, loading, selected, onSelect }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(models.length / MODELS_PER_PAGE);
  const visible = models.slice(page * MODELS_PER_PAGE, (page + 1) * MODELS_PER_PAGE);

  return (
    <div className="cto-field">
      <span className="cto-field-label">Model (optional)</span>
      <div className="cto-model-grid">
        <button
          className={`cto-model-card ${selected === null ? "cto-model-card-active" : ""}`}
          onClick={() => onSelect(null)}
          type="button"
        >
          <span className="cto-model-card-text">No Model</span>
        </button>
        {loading && <div className="cto-model-card-loading">Loading...</div>}
        {visible.map((m) => (
          <button
            key={m.id}
            className={`cto-model-card ${selected?.id === m.id ? "cto-model-card-active" : ""}`}
            onClick={() => onSelect(m)}
            type="button"
          >
            <img src={m.imageUrl} alt="Model" loading="lazy" />
          </button>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="cto-model-pager">
          <button
            className="cto-model-pager-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            ‹ Prev
          </button>
          <span className="cto-model-pager-info">{page + 1} / {totalPages}</span>
          <button
            className="cto-model-pager-btn"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────

export default function ClothingTryOnStudio({ studio }) {
  // Inputs
  const [clothingImages, setClothingImages] = useState([]); // array of URLs, max 4
  const [clothingUploading, setClothingUploading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null); // {id, imageUrl, metadata} or null
  const [environment, setEnvironment] = useState("studio_white");
  const [envImageUrl, setEnvImageUrl] = useState(null);
  const [envUploading, setEnvUploading] = useState(false);
  const [resolution, setResolution] = useState("2K");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [pose, setPose] = useState("anything");
  const [accessories, setAccessories] = useState("none");

  // Generations queue (same pattern as Character Creator)
  const [generations, setGenerations] = useState([]);
  const [selectedGenIndex, setSelectedGenIndex] = useState(-1);
  const [viewerData, setViewerData] = useState(null);

  const socketRef = useRef(null);
  const { models: characterModels, loading: modelsLoading } = useCharacterModels();

  const selectedGen = selectedGenIndex >= 0 ? generations[selectedGenIndex] : null;
  const anyRunning = generations.some((g) => g.status === "running");

  // ── File upload handlers ─────────────────────────────────────

  const handleClothingUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (clothingImages.length >= 4) return;
    setClothingUploading(true);
    try {
      const result = await uploadImage(file);
      const url = typeof result === "string" ? result : result?.image_url || result?.url || result?.data?.image_url || result?.data?.url;
      if (!url) throw new Error("Upload failed");
      setClothingImages((prev) => [...prev, url]);
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setClothingUploading(false);
    }
  };

  const removeClothingImage = (index) => {
    setClothingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEnvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnvUploading(true);
    try {
      const result = await uploadImage(file);
      const url = typeof result === "string" ? result : result?.image_url || result?.url || result?.data?.image_url || result?.data?.url;
      if (!url) throw new Error("Upload failed");
      setEnvImageUrl(url);
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setEnvUploading(false);
    }
  };

  // ── Socket + polling ─────────────────────────────────────────

  useEffect(() => {
    const socket = getUroSocket();
    if (!socket) return;
    socketRef.current = socket;

    const handleComplete = (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      fetchAndUpdateGeneration(jobId);
    };

    const handleFailed = (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      setGenerations((prev) =>
        prev.map((g) => g.id === jobId ? { ...g, status: "failed", error: payload?.failed_message || payload?.error || "Failed" } : g)
      );
    };

    const handleStepComplete = (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      if (payload?.node_id === "llm-enhancer") {
        setGenerations((prev) => prev.map((g) => g.id === jobId ? { ...g, enhancerDone: true } : g));
      }
    };

    socket.on("workflow:complete", handleComplete);
    socket.on("workflow:failed", handleFailed);
    socket.on("workflow:step:complete", handleStepComplete);

    return () => {
      socket.off("workflow:complete", handleComplete);
      socket.off("workflow:failed", handleFailed);
      socket.off("workflow:step:complete", handleStepComplete);
    };
  }, []);

  // Polling
  useEffect(() => {
    if (!anyRunning) return;
    const pollInterval = setInterval(async () => {
      for (const gen of generations.filter((g) => g.status === "running")) {
        try {
          const detail = await getStudioWorkflowJobDetail(gen.id);
          if (!detail) continue;
          if (detail.steps) {
            const enh = detail.steps.find((s) => s.workflow_step_id === "llm-enhancer" && s.status === "completed");
            if (enh) setGenerations((prev) => prev.map((g) => g.id === gen.id ? { ...g, enhancerDone: true } : g));
          }
          if (detail.status === "completed") fetchAndUpdateGeneration(gen.id);
          else if (detail.status === "failed") {
            setGenerations((prev) => prev.map((g) => g.id === gen.id ? { ...g, status: "failed", error: detail.error_message || "Failed" } : g));
          }
        } catch (_) {}
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [anyRunning, generations]);

  const fetchAndUpdateGeneration = useCallback(async (jobId) => {
    try {
      const jobData = await getStudioWorkflowJob(jobId);
      const resultsArr = jobData?.results?.results || jobData?.results;
      const imageUrl = Array.isArray(resultsArr) && resultsArr.length > 0
        ? resultsArr[0].content || resultsArr[0].external_url : null;
      setGenerations((prev) => prev.map((g) => g.id === jobId ? { ...g, status: "completed", imageUrl, jobData } : g));
    } catch (_) {
      setGenerations((prev) => prev.map((g) => g.id === jobId ? { ...g, status: "completed", imageUrl: null } : g));
    }
  }, []);

  // ── Generate ─────────────────────────────────────────────────

  const canGenerate = clothingImages.length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    try {
      const inputParams = {
        clothing_image_urls: clothingImages,
        model_image_url: selectedModel?.imageUrl || undefined,
        model_metadata: selectedModel?.metadata || undefined,
        environment,
        environment_image_url: environment === "custom" ? envImageUrl : undefined,
        resolution,
        aspect_ratio: aspectRatio,
        pose,
        accessories,
      };

      const runResult = await runStudioWorkflow(WORKFLOW_ID, inputParams);

      const newGen = {
        id: runResult.workflow_job_id,
        status: "running",
        imageUrl: null,
        jobData: null,
        enhancerDone: false,
        error: null,
      };

      setGenerations((prev) => [newGen, ...prev]);
      setSelectedGenIndex(0);
    } catch (err) {
      alert(err.message || "Failed to start");
    }
  };

  const handleImageClick = () => {
    if (!selectedGen?.jobData) return;
    setViewerData(selectedGen.jobData);
  };

  const mainGen = selectedGen;

  return (
    <div className="cto-studio">
      <StudioHero title={studio.name} subtitle="Studio" description={studio.description} />

      <div className="cto-layout">
        {/* ── Left: inputs ──────────────────────────────────── */}
        <div className="cto-form-panel">
          <div className="cto-form-scroll">
            {/* Clothing images upload (1-4) */}
            <div className="cto-field">
              <span className="cto-field-label">Clothing Images * ({clothingImages.length}/4)</span>
              {clothingImages.length > 0 && (
                <div className="cto-clothing-grid">
                  {clothingImages.map((url, idx) => (
                    <div key={idx} className="cto-clothing-thumb">
                      <img src={url} alt={`Clothing ${idx + 1}`} />
                      <button className="cto-image-clear" onClick={() => removeClothingImage(idx)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {clothingImages.length < 4 && (
                <div className="cto-upload">
                  <input type="file" accept="image/*" onChange={handleClothingUpload} disabled={clothingUploading} />
                  {clothingUploading && <div className="cto-upload-hint">Uploading...</div>}
                </div>
              )}
            </div>

            {/* Model selection from Character Creator */}
            <ModelSelector
              models={characterModels}
              loading={modelsLoading}
              selected={selectedModel}
              onSelect={setSelectedModel}
            />

            {/* Environment */}
            <ChipSelector label="Environment" options={ENVIRONMENTS} value={environment} onChange={setEnvironment} />

            {environment === "custom" && (
              <div className="cto-field">
                <span className="cto-field-label">Environment Image</span>
                {envImageUrl ? (
                  <div className="cto-image-preview">
                    <img src={envImageUrl} alt="Environment" />
                    <button className="cto-image-clear" onClick={() => setEnvImageUrl(null)}>×</button>
                  </div>
                ) : (
                  <div className="cto-upload">
                    <input type="file" accept="image/*" onChange={handleEnvUpload} disabled={envUploading} />
                    {envUploading && <div className="cto-upload-hint">Uploading...</div>}
                  </div>
                )}
              </div>
            )}

            {/* Pose — visual grid */}
            <PoseGrid options={POSES} value={pose} onChange={setPose} />

            {/* Accessories */}
            <ChipSelector label="Accessories" options={ACCESSORIES} value={accessories} onChange={setAccessories} />

            {/* Resolution + Aspect Ratio */}
            <ChipSelector label="Resolution" options={RESOLUTIONS} value={resolution} onChange={setResolution} />
            <ChipSelector label="Aspect Ratio" options={ASPECT_RATIOS} value={aspectRatio} onChange={setAspectRatio} />
          </div>

          <div className="cto-form-actions">
            <StudioGenerateButton onClick={handleGenerate} disabled={!canGenerate} loading={false} />
          </div>
        </div>

        {/* ── Right: canvas + gen strip ──────────────────────── */}
        <div className="cto-right-panel">
          <div className="cto-canvas-panel">
            {!mainGen && (
              <div className="cto-empty">
                <div className="cto-empty-icon">👗</div>
                <div className="cto-empty-text">Your try-on result will appear here</div>
              </div>
            )}
            {mainGen?.status === "running" && (
              <div className="cto-loading">
                <div className="cto-loading-spinner" />
                <div className="cto-loading-text">
                  {mainGen.enhancerDone ? "Generating image..." : "Analyzing clothing & planning..."}
                </div>
              </div>
            )}
            {mainGen?.status === "failed" && (
              <div className="cto-error">
                <div className="cto-error-icon">⚠</div>
                <div className="cto-error-text">{mainGen.error || "Failed"}</div>
              </div>
            )}
            {mainGen?.status === "completed" && mainGen.imageUrl && (
              <div className="cto-result" onClick={handleImageClick}>
                <img src={mainGen.imageUrl} alt="Result" className="cto-result-image" draggable={false} />
              </div>
            )}
          </div>

          {generations.length > 0 && (
            <div className="cto-gen-strip">
              {generations.map((gen, idx) => (
                <button
                  key={gen.id}
                  className={`cto-gen-card ${idx === selectedGenIndex ? "cto-gen-card-active" : ""}`}
                  onClick={() => setSelectedGenIndex(idx)}
                  type="button"
                >
                  {gen.status === "running" && <div className="cto-gen-card-spinner" />}
                  {gen.status === "failed" && <div className="cto-gen-card-failed">⚠</div>}
                  {gen.status === "completed" && gen.imageUrl && <img src={gen.imageUrl} alt="" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <JobDetailModal visible={!!viewerData} data={viewerData} onClose={() => setViewerData(null)} />
    </div>
  );
}
